import type { ReactElement } from 'react';
import {
    buttonVariants,
    PolymorphicButtonBase,
    type ButtonVariantProps,
    type PolymorphicButtonBaseProps,
} from '../primitives/button.tsx';
import { cn } from '../util/classes.ts';

/**
 * Property type for {@link IconButton}.
 */
export type IconButtonProps = ButtonVariantProps &
    PolymorphicButtonBaseProps & {
        /** Names the action for assistive technology, since no text is visible. */
        label: string;
    };

/**
 * A button whose only content is an icon: the same variants and intents as
 * `Button`, squared off to the interactive size with no room for a label.
 *
 * ```tsx
 * <IconButton label="Dismiss">
 *     <XIcon />
 * </IconButton>
 * ```
 */
export function IconButton({ className, intent, label, variant, ...props }: IconButtonProps): ReactElement {
    return (
        <PolymorphicButtonBase
            aria-label={label}
            className={cn(
                buttonVariants({ variant, intent }),
                // no visible label means no room for one: drop the horizontal
                // padding and let `min-w-interactive` square the button off
                'px-0',
                "*:[svg:not([class*='size-'])]:size-4",
                className,
            )}
            {...props}
        />
    );
}
